import { ArrowRightIcon, CaretRightIcon, InfoIcon } from '@phosphor-icons/react'
import * as Collapsible from '@radix-ui/react-collapsible'
import * as HoverCard from '@radix-ui/react-hover-card'
import classNames from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { DropdownFilter, type Filter } from '../../components/dropdown-filter.tsx'
import type {
  ThreatModelActor,
  ThreatModelContent,
  ThreatModelDependencyNote,
  ThreatModelEntryPoint,
  ThreatModelSecurityControl,
  ThreatModelSensitiveAsset,
  ThreatModelTrustBoundary,
  ThreatModelVulnerabilityImpact,
} from '../../utils/api-client/api-schemas.ts'
import { coerceInternetFacing, normalise } from '../../utils/threat-model/exposure.ts'
import { adjudicationSearch, surfaceSectionSearch, type SurfaceSectionId } from './threat-model-navigation.ts'
import * as styles from './threat-model-report.css.ts'
import * as previewStyles from './threat-model-stat-preview.css.ts'
import { ThreatModelStatPreview, type StatPreviewItem } from './threat-model-stat-preview.tsx'

// Re-exported so `exposure-chip.tsx` keeps its existing import path; the definition is shared.
export { coerceInternetFacing }

/**
 * The threat model as a report, rendered from the typed `/content` payload.
 *
 * Replaces the markdown view. `threat-model-sections.ts` recovered structure by guessing at prose shape;
 * with `ThreatModelPayload` every field is named, so nothing is inferred and nothing is dropped — the old
 * path silently discarded the prose beside a table, because a section could only be one shape.
 *
 * **Section order follows the payload, not a ranking of our own.** The generator orders deliberately —
 * context, then inventory, then adjudication, then caveats — and the previous view's habit of hoisting
 * diagrams and tables above the framing prose was the clearest thing wrong with it.
 */

const KIND_LABELS: Record<string, string> = {
  http_route: 'HTTP route',
  cli: 'CLI',
  message_queue: 'Message queue',
  file_upload: 'File upload',
  webhook: 'Webhook',
  rpc: 'RPC',
  cron: 'Cron',
  environment: 'Environment',
  proprietary_data: 'Proprietary data',
  infrastructure: 'Infrastructure',
  credential: 'Credential',
  pii: 'PII',
  financial: 'Financial',
  secret: 'Secret',
  token: 'Token',
  output_encoding: 'Output encoding',
  templating_autoescape: 'Templating autoescape',
  content_security_policy: 'Content security policy',
  csrf_protection: 'CSRF protection',
  parameterized_queries: 'Parameterized queries',
  input_validation: 'Input validation',
  sanitization: 'Sanitization',
  authentication: 'Authentication',
  authorization: 'Authorization',
  secrets_management: 'Secrets management',
  transport_security: 'Transport security',
  rate_limiting: 'Rate limiting',
  sandboxing: 'Sandboxing',
  waf: 'WAF',
  other: 'Other',
}

/** Enum values are snake_case on the wire; nothing should render underscores. */
const kindLabel = (kind: string): string => KIND_LABELS[kind] ?? kind.replace(/_/g, ' ')

export type { Relevance }

export type ThreatModelReportProps = {
  content: ThreatModelContent
}

/**
 * The document splits into three views because its sections answer three different questions, on three
 * different rhythms.
 *
 * **Overview** is read once to get your bearings. **Attack surface** is the structured inventory, read
 * while moving between the model and the code — every section in it carries a `location`. **Adjudication**
 * is the reason the document exists: the model's own docstring calls it "a security-context reference…
 * consulted when a specific vulnerability is discovered later", so it is arrived at knowing which class
 * you want, not scrolled to.
 */
export const ThreatModelOverview: React.FC<ThreatModelReportProps> = ({ content }) => {
  const model = content

  return (
    <article className={styles.report} aria-label="Threat model overview">
      <Masthead content={content} />

      <Section heading="System overview">
        <p className={styles.sectionLead}>{model.system_overview}</p>
      </Section>

      <Section
        heading="Exposure"
        tag={
          <span
            className={styles.exposureTag({ internetFacing: coerceInternetFacing(model.exposure.internet_facing) })}
          >
            {`Internet-facing: ${model.exposure.internet_facing}`}
          </span>
        }
      >
        <ExposureChain content={content} />
      </Section>

      <Section heading="Access model">
        <div className={styles.proseGrid}>
          <LabelledProse label="Authentication" text={model.authentication_model} />
          <LabelledProse label="Authorization" text={model.authorization_model} />
        </div>
      </Section>

      {/* Assumptions qualify everything above, so they close the orientation rather than getting a home of
          their own where nobody would look for them. */}
      {model.assumptions.length > 0 && (
        <Section heading="Assumptions" count={model.assumptions.length}>
          <ul className={styles.assumptionList}>
            {model.assumptions.map(assumption => (
              <li key={assumption} className={styles.assumptionItem}>
                {assumption}
              </li>
            ))}
          </ul>
        </Section>
      )}
    </article>
  )
}

/**
 * The five typed inventories, led by the prose that describes them.
 *
 * `attack_surface_summary` narrates exactly these sections — it names actors and their reach, entry points,
 * the controls in place and what crosses the boundaries — so it belongs at the head of them rather than
 * stranded among the framing prose. `data_flow_notes` does the same for the path from input to sink.
 */
export const ThreatModelAttackSurface: React.FC<ThreatModelReportProps & { openSection?: SurfaceSectionId }> = ({
  content,
  openSection,
}) => {
  const model = content

  return (
    <article className={styles.report} aria-label="Threat model attack surface">
      {/* "Summary", not "Attack surface": the tab above already says that, and a section repeating its
          parent's name reads as a nested duplicate rather than the opening paragraph it is. */}
      <Section heading="Summary">
        <p className={styles.sectionLead}>{model.attack_surface_summary}</p>
      </Section>

      <Section heading="Data flow">
        <p className={styles.sectionLead}>{model.data_flow_notes}</p>
      </Section>

      <Section isCollapsible heading="Actors" count={model.actors.length} isTargeted={openSection === 'actors'}>
        <div className={styles.inventory}>
          {/* `name` is free text the generator can repeat and actors carry no `location`, so the
              position disambiguates the key — same reasoning as the masthead preview. */}
          {model.actors.map((actor, index) => (
            <ActorRow key={`${actor.name}-${index}`} actor={actor} />
          ))}
        </div>
      </Section>

      <Section
        isCollapsible
        heading="Entry points"
        count={model.entry_points.length}
        isTargeted={openSection === 'entry-points'}
      >
        <div className={styles.inventory}>
          {model.entry_points.map(entryPoint => (
            <EntryPointRow key={`${entryPoint.name}-${entryPoint.location}`} entryPoint={entryPoint} />
          ))}
        </div>
      </Section>

      <Section
        isCollapsible
        heading="Trust boundaries"
        count={model.trust_boundaries.length}
        isTargeted={openSection === 'trust-boundaries'}
      >
        <div className={styles.inventory}>
          {/* Trust boundaries likewise have no `location`, so the position keeps the key unique. */}
          {model.trust_boundaries.map((boundary, index) => (
            <TrustBoundaryRow key={`${boundary.name}-${index}`} boundary={boundary} />
          ))}
        </div>
      </Section>

      <Section
        isCollapsible
        heading="Sensitive assets"
        count={model.sensitive_assets.length}
        isTargeted={openSection === 'sensitive-assets'}
      >
        <div className={styles.inventory}>
          {model.sensitive_assets.map(asset => (
            <SensitiveAssetRow key={`${asset.name}-${asset.location}`} asset={asset} />
          ))}
        </div>
      </Section>

      <Section
        isCollapsible
        heading="Security controls"
        count={model.security_controls.length}
        isTargeted={openSection === 'security-controls'}
      >
        <div className={styles.inventory}>
          {model.security_controls.map(control => (
            <SecurityControlRow key={`${control.name}-${control.location}`} control={control} />
          ))}
        </div>
      </Section>

      {/* Dependency notes qualify the surface rather than constituting one, so they close it. */}
      <Section
        isCollapsible
        heading="Dependency notes"
        count={model.dependency_notes.length}
        isTargeted={openSection === 'dependency-notes'}
      >
        <div className={styles.inventory}>
          {model.dependency_notes.map(note => (
            <DependencyNoteRow key={`${note.name}-${note.location}`} note={note} />
          ))}
        </div>
      </Section>
    </article>
  )
}

/**
 * The categorical fields arrive as free text, not enums.
 *
 * `api-schemas.ts` types `trust_level`, `relevance` and `internet_facing` as `z.string()` on purpose:
 * "the values are generated per model and the UI degrades gracefully on ones it does not recognise".
 * Each tag therefore normalises and falls back rather than indexing a recipe with an unknown key, which
 * would render an unstyled tag. The fallbacks are the least-committal value in each vocabulary — an
 * unrecognised value must never read as a stronger claim than the model made.
 *
 * `normalise` comes from `utils/threat-model/exposure.ts`, which owns it: these vocabularies bucket input
 * the same way, and two copies of the rule could start disagreeing about what counts as the same value.
 */
const TRUST_LEVELS = ['anonymous', 'authenticated', 'privileged', 'service', 'internal'] as const
type TrustLevel = (typeof TRUST_LEVELS)[number]
const coerceTrustLevel = (value: string): TrustLevel =>
  TRUST_LEVELS.find(level => level === normalise(value)) ?? 'internal'

const RELEVANCES = ['present', 'likely', 'unlikely', 'not_applicable'] as const
type Relevance = (typeof RELEVANCES)[number]
const coerceRelevance = (value: string): Relevance =>
  RELEVANCES.find(relevance => relevance === normalise(value)) ?? 'unlikely'

const RELEVANCE_ORDER: readonly Relevance[] = RELEVANCES

const RELEVANCE_LABELS: Record<Relevance, string> = {
  present: 'Present',
  likely: 'Likely',
  unlikely: 'Unlikely',
  not_applicable: 'Not applicable',
}

/**
 * The per-class guidance, which is what a reader arrives for once a finding exists.
 *
 * Filtered by **relevance** rather than "presence": the field is `relevance`, and its four values do not
 * describe degrees of being present — `not_applicable` is a judgement that the class does not arise here,
 * and `likely`/`unlikely` are about whether it would matter, not whether it exists.
 */
export type RelevanceSelection = {
  selected: Relevance[]
  onChange: (selected: Relevance[]) => void
}

/**
 * The relevance filter, exported so it can sit in the meta row beside Regenerate rather than inside the
 * section it filters. The selection therefore lives above both — see `ThreatModelDocumentView`.
 */
export const RelevanceFilter: React.FC<{ content: ThreatModelContent } & RelevanceSelection> = ({
  content,
  selected,
  onChange,
}) => {
  const counts = useRelevanceCounts(content.vulnerability_class_impacts)
  // Only offer values this document actually uses; a filter for a bucket that is always empty is noise.
  const availableRelevances = RELEVANCE_ORDER.filter(relevance => (counts[relevance] ?? 0) > 0)

  const toggle = (relevance: Relevance) =>
    onChange(selected.includes(relevance) ? selected.filter(entry => entry !== relevance) : [...selected, relevance])

  const filters: Filter[] = availableRelevances.map(relevance => ({
    type: 'checkbox',
    id: relevance,
    text: RELEVANCE_LABELS[relevance],
    count: counts[relevance],
    value: selected.includes(relevance),
    onToggle: () => toggle(relevance),
  }))

  return (
    <DropdownFilter
      label="Filter by relevance"
      align="end"
      filterElements={[
        {
          type: 'group',
          id: 'relevance',
          text: 'Relevance',
          filters,
          onToggleGroup: () => onChange(selected.length > 0 ? [] : availableRelevances),
        },
      ]}
      isAnyNonDefaultFilterActive={selected.length > 0}
      onToggleAllFiltersOff={() => onChange([])}
    />
  )
}

const useRelevanceCounts = (impacts: ThreatModelVulnerabilityImpact[]) =>
  useMemo(
    () =>
      impacts.reduce<Partial<Record<Relevance, number>>>((totals, impact) => {
        const relevance = coerceRelevance(impact.relevance)
        return { ...totals, [relevance]: (totals[relevance] ?? 0) + 1 }
      }, {}),
    [impacts]
  )

/**
 * The per-class guidance, which is what a reader arrives for once a finding exists.
 *
 * Filtered by **relevance** rather than "presence": the field is `relevance`, and its four values do not
 * describe degrees of being present — `not_applicable` is a judgement that the class does not arise here,
 * and `likely`/`unlikely` are about whether it would matter, not whether it exists.
 *
 * `selected` is owned by the caller so the control can live in the meta row; an empty selection is no
 * filter at all, not "everything hidden".
 */
export const ThreatModelAdjudication: React.FC<ThreatModelReportProps & { selected?: Relevance[] }> = ({
  content,
  selected = [],
}) => {
  const impacts = content.vulnerability_class_impacts
  // Bucket on the coerced value, not the raw string: free text means "Present" and "present" would
  // otherwise be two buckets and the filter would silently drop one of them.
  const visible =
    selected.length === 0 ? impacts : impacts.filter(impact => selected.includes(coerceRelevance(impact.relevance)))

  return (
    <article className={styles.report} aria-label="Threat model adjudication guidance">
      <Section heading="Adjudication guidance" count={visible.length}>
        {visible.length === 0 ? (
          <p className={styles.sectionLead}>No vulnerability classes match the selected relevance.</p>
        ) : (
          <div className={styles.adjudicationGrid}>
            {/* Keyed by position as well as class: `vuln_class` is free text and the generator does
                repeat it — `threat-coverage.ts` merges the duplicates on purpose — so the class alone
                is not a unique key and React would drop every card after the first of a name. */}
            {visible.map((impact, index) => (
              <AdjudicationCard key={`${impact.vuln_class}-${index}`} impact={impact} />
            ))}
          </div>
        )}
      </Section>
    </article>
  )
}

type SectionProps = {
  heading: string
  count?: number
  /** Sits against the heading text — a single fact about the section, not a control. */
  tag?: React.ReactNode
  /** Right-justified against the heading — a control that acts on this section, not on the page. */
  action?: React.ReactNode
  /**
   * For the inventories, which run to 4–11 rows each and stack into a very long page. Open by default —
   * collapsing is for getting a section out of the way, not for hiding it until asked.
   */
  isCollapsible?: boolean
  /** This is the section an Overview metric linked to: force it open and move focus to it. */
  isTargeted?: boolean
  children: React.ReactNode
}

const Section: React.FC<SectionProps> = ({ heading, count, tag, action, isCollapsible, isTargeted, children }) => {
  const label = (
    <>
      {heading}
      {count !== undefined && ` (${count})`}
    </>
  )

  if (!isCollapsible) {
    return (
      <section className={styles.section} aria-label={heading}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeadingGroup}>
            <h3 className={styles.sectionHeading}>{label}</h3>
            {tag}
          </div>
          {action}
        </div>
        {children}
      </section>
    )
  }

  return (
    <CollapsibleSection heading={heading} label={label} tag={tag} action={action} isTargeted={isTargeted}>
      {children}
    </CollapsibleSection>
  )
}

type CollapsibleSectionProps = {
  heading: string
  label: React.ReactNode
  tag?: React.ReactNode
  action?: React.ReactNode
  isTargeted?: boolean
  children: React.ReactNode
}

/**
 * Split out from `Section` because it needs hooks and the non-collapsible branch returns before them.
 *
 * Open is controlled rather than `defaultOpen` so that arriving from an Overview metric re-opens a
 * section the reader had collapsed — a link that lands on a closed section looks like it did nothing.
 */
const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  heading,
  label,
  tag,
  action,
  isTargeted,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(true)
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!isTargeted) {
      return
    }
    setIsOpen(true)
    const node = sectionRef.current
    if (!node) {
      return
    }
    // Focus for a11y (it moves the keyboard position and announces the landing place), but suppress
    // the browser's implicit focus-scroll and do the scroll ourselves. Left to `focus()`, the scroll
    // uses `block: 'nearest'`, which lands inconsistently across sections of different heights — it
    // overshoots a tall section (past its header) and undershoots one already near the top. A explicit
    // `block: 'start'` puts every targeted section's header at the same place.
    node.focus({ preventScroll: true })
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    node.scrollIntoView?.({ block: 'start', behavior: prefersReducedMotion ? 'auto' : 'smooth' })
  }, [isTargeted])

  return (
    <Collapsible.Root open={isOpen} onOpenChange={setIsOpen} asChild>
      <section
        ref={sectionRef}
        // Focusable only as a link target, never in the tab order.
        tabIndex={-1}
        className={classNames(styles.section, isTargeted && styles.sectionTargeted)}
        aria-label={heading}
      >
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeadingGroup}>
            {/* The button sits inside the h3, not around it — `button` takes phrasing content only, so a
                heading inside a trigger is invalid markup and costs the heading its role. */}
            <h3 className={styles.sectionHeading}>
              <Collapsible.Trigger className={styles.collapseTrigger}>
                <CaretRightIcon className={styles.caretIcon} size={20} weight="light" />
                {label}
              </Collapsible.Trigger>
            </h3>
            {tag}
          </div>
          {action}
        </div>
        <Collapsible.Content className={styles.collapsibleContent}>{children}</Collapsible.Content>
      </section>
    </Collapsible.Root>
  )
}

const Masthead: React.FC<{ content: ThreatModelContent }> = ({ content }) => {
  const [searchParams] = useSearchParams()

  // Every metric addresses the place that holds the things it counts. Five are Lists inventories; vuln
  // classes are adjudicated one card per class rather than inventoried, so that one addresses a view.
  // `previewItems` is the whole inventory, mapped to a name and a one-line descriptor; the hovercard
  // slices it to the top N. All of it comes from the already-fetched payload — the preview never fetches.
  const stats = [
    {
      label: 'Actors',
      value: content.actors.length,
      destination: 'Attack surface',
      section: 'actors',
      // Actors carry no `location` to composite with, and `name` is free text the generator can
      // repeat, so the position disambiguates the React key the way it does for vuln classes below.
      previewItems: content.actors.map((actor, index) => ({
        key: `${actor.name}-${index}`,
        primary: actor.name,
        secondary: actor.can_reach,
      })),
    },
    {
      label: 'Entry points',
      value: content.entry_points.length,
      destination: 'Attack surface',
      section: 'entry-points',
      previewItems: content.entry_points.map(entryPoint => ({
        key: `${entryPoint.name}-${entryPoint.location}`,
        primary: entryPoint.name,
        secondary: kindLabel(entryPoint.kind),
      })),
    },
    {
      label: 'Boundaries',
      value: content.trust_boundaries.length,
      destination: 'Attack surface',
      section: 'trust-boundaries',
      // Trust boundaries likewise have no `location`, so the position keeps the key unique.
      previewItems: content.trust_boundaries.map((boundary, index) => ({
        key: `${boundary.name}-${index}`,
        primary: boundary.name,
        secondary: boundary.crosses,
      })),
    },
    {
      label: 'Sensitive assets',
      value: content.sensitive_assets.length,
      destination: 'Attack surface',
      section: 'sensitive-assets',
      previewItems: content.sensitive_assets.map(asset => ({
        key: `${asset.name}-${asset.location}`,
        primary: asset.name,
        secondary: kindLabel(asset.kind),
      })),
    },
    {
      label: 'Controls',
      value: content.security_controls.length,
      destination: 'Attack surface',
      section: 'security-controls',
      previewItems: content.security_controls.map(control => ({
        key: `${control.name}-${control.location}`,
        primary: control.name,
        secondary: kindLabel(control.kind),
      })),
    },
    {
      label: 'Vuln classes',
      value: content.vulnerability_class_impacts.length,
      destination: 'Adjudication',
      previewItems: content.vulnerability_class_impacts.map((impact, index) => ({
        // `vuln_class` is free text and the generator repeats it, so the class alone is not a unique key.
        key: `${impact.vuln_class}-${index}`,
        primary: impact.vuln_class,
        secondary: RELEVANCE_LABELS[coerceRelevance(impact.relevance)],
      })),
    },
  ] satisfies {
    label: string
    value: number
    destination: string
    section?: SurfaceSectionId
    previewItems: StatPreviewItem[]
  }[]

  // No provenance line here: `ThreatModelMetaRow` already carries generated-at, commit and requester
  // directly above this content, and `ThreatModelContent` has no envelope to read them from anyway.
  return (
    <header className={styles.masthead}>
      <div className={styles.statRow}>
        {stats.map(stat => {
          // An empty inventory has nothing to land on, so it does not become a link — and with nothing
          // behind the count, there is nothing to preview either.
          if (stat.value === 0) {
            return (
              <div key={stat.label} className={styles.stat}>
                <p className={styles.statValue}>{stat.value}</p>
                <p className={styles.statLabel}>{stat.label}</p>
              </div>
            )
          }

          const to = {
            search: stat.section ? surfaceSectionSearch(searchParams, stat.section) : adjudicationSearch(searchParams),
          }

          return (
            <HoverCard.Root key={stat.label} openDelay={120} closeDelay={120}>
              <HoverCard.Trigger asChild>
                <Link
                  to={to}
                  className={classNames(styles.stat, styles.statLink)}
                  aria-label={`${stat.value} ${stat.label.toLowerCase()} — open in ${stat.destination}`}
                >
                  <p className={styles.statValue}>{stat.value}</p>
                  <p className={styles.statLabel}>
                    {stat.label}
                    {/* The standing cue that this figure goes somewhere. Dim at rest so six of them do
                        not clutter the row, and it is the one visible difference from a metric that
                        does not. */}
                    <ArrowRightIcon className={styles.statLinkArrow} size={12} weight="bold" aria-hidden />
                  </p>
                </Link>
              </HoverCard.Trigger>
              <HoverCard.Portal>
                <HoverCard.Content side="bottom" align="start" sideOffset={6} className={previewStyles.hoverContent}>
                  <ThreatModelStatPreview
                    label={stat.label}
                    destination={stat.destination}
                    total={stat.value}
                    items={stat.previewItems}
                    to={to}
                  />
                </HoverCard.Content>
              </HoverCard.Portal>
            </HoverCard.Root>
          )
        })}
      </div>
    </header>
  )
}

/** Same panel, label and prose treatment as the exposure chain — these read as the same kind of thing. */
const LabelledProse: React.FC<{ label: string; text: string }> = ({ label, text }) => (
  <div className={styles.proseCard}>
    <p className={styles.proseCardLabel}>{label}</p>
    <p className={styles.sectionLead}>{text}</p>
  </div>
)

/**
 * Exposure as the chain it describes: where the system runs, then how that placement leaves it reachable.
 *
 * The two fields are causally ordered — `network_position` is a consequence of `deployment_environment` — so
 * a connector says something true that four equal cards did not. `notes` is not a third step; it qualifies
 * the chain, and reads as an aside beneath it.
 *
 * `internet_facing` is a tag on the heading rather than a panel of its own: it is one enum word, it is
 * already in the masthead line, and it was `partial` in all six real documents — a large graphic for it
 * would render identically on every repository.
 */
const ExposureChain: React.FC<{ content: ThreatModelContent }> = ({ content }) => {
  const { exposure } = content
  return (
    <div className={styles.exposure}>
      <div className={styles.chain}>
        <div className={styles.proseCard}>
          <p className={styles.proseCardLabel}>Runs as</p>
          <p className={styles.sectionLead}>{exposure.deployment_environment}</p>
        </div>
        <div className={styles.connector} aria-hidden>
          <span className={styles.connectorLine} />
          <ArrowRightIcon className={styles.connectorArrow} size={18} weight="bold" />
        </div>
        <div className={styles.proseCard}>
          <p className={styles.proseCardLabel}>Sits on the network</p>
          <p className={styles.sectionLead}>{exposure.network_position}</p>
        </div>
      </div>
      {exposure.notes && (
        <div className={styles.caveat}>
          <InfoIcon size={16} weight="regular" className={styles.caveatIcon} />
          <p className={styles.proseMuted}>{exposure.notes}</p>
        </div>
      )}
    </div>
  )
}

const ActorRow: React.FC<{ actor: ThreatModelActor }> = ({ actor }) => (
  <div className={styles.inventoryRow}>
    <div className={styles.inventoryHeader}>
      <p className={styles.inventoryName}>{actor.name}</p>
      <span
        className={styles.trustTag({ level: coerceTrustLevel(actor.trust_level) })}
        data-trust-level={actor.trust_level}
      >
        {actor.trust_level}
      </span>
    </div>
    <p className={styles.inventoryBody}>{actor.can_reach}</p>
    <p className={styles.inventoryAside}>Authenticates via {actor.authenticates_via}</p>
  </div>
)

const EntryPointRow: React.FC<{ entryPoint: ThreatModelEntryPoint }> = ({ entryPoint }) => (
  <div className={styles.inventoryRow}>
    <div className={styles.inventoryHeader}>
      <p className={styles.inventoryName}>{entryPoint.name}</p>
      <span className={styles.kindTag}>{kindLabel(entryPoint.kind)}</span>
    </div>
    {/* The description is the part the markdown view dropped when a section held both a table and prose. */}
    <p className={styles.inventoryBody}>{entryPoint.description}</p>
    <p className={styles.inventoryAside}>Auth: {entryPoint.authentication}</p>
    <p className={styles.location}>{entryPoint.location}</p>
  </div>
)

const TrustBoundaryRow: React.FC<{ boundary: ThreatModelTrustBoundary }> = ({ boundary }) => (
  <div className={styles.inventoryRow}>
    <p className={styles.inventoryName}>{boundary.name}</p>
    <p className={styles.inventoryBody}>{boundary.description}</p>
    <p className={styles.inventoryAside}>Crosses: {boundary.crosses}</p>
  </div>
)

const SensitiveAssetRow: React.FC<{ asset: ThreatModelSensitiveAsset }> = ({ asset }) => (
  <div className={styles.inventoryRow}>
    <div className={styles.inventoryHeader}>
      <p className={styles.inventoryName}>{asset.name}</p>
      <span className={styles.kindTag}>{kindLabel(asset.kind)}</span>
    </div>
    <p className={styles.inventoryBody}>{asset.description}</p>
    <p className={styles.location}>{asset.location}</p>
  </div>
)

const SecurityControlRow: React.FC<{ control: ThreatModelSecurityControl }> = ({ control }) => (
  <div className={styles.inventoryRow}>
    <div className={styles.inventoryHeader}>
      <p className={styles.inventoryName}>{control.name}</p>
      <span className={styles.kindTag}>{kindLabel(control.kind)}</span>
    </div>
    <p className={styles.inventoryBody}>{control.scope}</p>
    {control.mitigates.length > 0 && (
      <div className={styles.tagRow}>
        <span className={styles.inventoryAside}>Mitigates:</span>
        {control.mitigates.map(item => (
          <span key={item} className={styles.kindTag}>
            {item}
          </span>
        ))}
      </div>
    )}
    <p className={styles.location}>{control.location}</p>
  </div>
)

const DependencyNoteRow: React.FC<{ note: ThreatModelDependencyNote }> = ({ note }) => (
  <div className={styles.inventoryRow}>
    <p className={styles.inventoryName}>{note.name}</p>
    <p className={styles.inventoryBody}>{note.reason}</p>
    <p className={styles.location}>{note.location}</p>
  </div>
)

const AdjudicationCard: React.FC<{ impact: ThreatModelVulnerabilityImpact }> = ({ impact }) => (
  <div className={styles.adjudicationCard}>
    <div className={styles.adjudicationHeader}>
      <p className={styles.adjudicationTitle}>{impact.vuln_class}</p>
      <span
        className={styles.relevanceTag({ relevance: coerceRelevance(impact.relevance) })}
        data-relevance={impact.relevance}
      >
        {impact.relevance}
      </span>
    </div>
    {[
      { label: 'Reachability', text: impact.reachability },
      { label: 'Blast radius', text: impact.blast_radius },
      { label: 'Mitigating controls', text: impact.mitigating_controls },
    ].map(field => (
      <div key={field.label} className={styles.adjudicationField}>
        <p className={styles.adjudicationLabel}>{field.label}</p>
        <p className={styles.adjudicationText}>{field.text}</p>
      </div>
    ))}
    <div className={styles.verifyField}>
      <p className={styles.adjudicationLabel}>Verify</p>
      <p className={styles.adjudicationText}>{impact.verify}</p>
    </div>
  </div>
)
